<!-- \src\components\StrategyComponent.vue -->
<template>
  <div class="container mt-3">
    <div class="accordion" :id="`strategy-${localStrategy.id}`">
      <div class="accordion-item">
        <h2 class="accordion-header" :id="`heading-${localStrategy.id}`">
          <button
            class="accordion-button collapsed"
            type="button"
            data-bs-toggle="collapse"
            :data-bs-target="`#collapse-${localStrategy.id}`"
            :aria-controls="`collapse-${localStrategy.id}`"
          >
            <div
              class="d-flex justify-content-between align-items-center w-100"
            >
              <div>
                <strong class="hidden-xs">{{ localStrategy.name }}</strong>
              </div>
            </div>

            <div class="btn-group me-3">
              <i
                class="fa fa-copy icon-medium"
                role="button"
                data-bs-toggle="modal"
                :data-bs-target="`#copyStrategyModal-${localStrategy.id}`"
                title="Copy Strategy"
              ></i>
              <i
                class="fa fa-save icon-medium"
                :class="{
                  'strategy-action-icon--disabled': isNameNotValid,
                }"
                role="button"
                title="Save Strategy"
                @click.stop="!isNameNotValid && saveStrategy(true)"
                :disabled="isNameNotValid"
              ></i>
              <i
                class="fa fa-trash icon-medium"
                @click="deleteStrategy"
                role="button"
                title="Delete Strategy"
              ></i>
            </div>
          </button>
        </h2>
        <div
          :id="`collapse-${localStrategy.id}`"
          class="accordion-collapse collapse"
          :aria-labelledby="`heading-${localStrategy.id}`"
          :data-bs-parent="`#strategy-${localStrategy.id}`"
        >
          <div class="accordion-body">
            <form class="strategy-options-form fluid">
              <div class="row mb-3">
                <div class="col-lg-5 col-md-5 col-sm-12">
                  <div class="form-group">
                    <label for="strategyName" class="form-label"
                      >Strategy Name</label
                    >
                    <input
                      type="text"
                      class="form-control"
                      id="strategyName"
                      v-model="localStrategy.name"
                      maxlength="40"
                      autofocus
                    />
                    <!-- Red warning if name is duplicated -->
                    <div v-if="isNameNotValid" class="text-danger mt-1">
                      Strategy with such name already exists.
                    </div>
                  </div>
                </div>
                <div
                  class="col-lg-5 col-md-5 col-sm-12 offset-lg-1 offset-md-1"
                >
                  <div class="form-group">
                    <label for="priceRounding" class="form-label"
                      >Price Rounding</label
                    >
                    <select
                      class="form-control"
                      id="priceRounding"
                      v-model="localStrategy.roundingTypeId"
                    >
                      <option value="0">To nearest cent</option>
                      <option value="1">To nearest dollar</option>
                      <option value="2">To nearest 99c</option>
                      <option value="3">To nearest higher dollar</option>
                      <option value="4">To nearest higher 99c</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-lg-6 col-md-7 col-sm-12">
                  <div class="form-check form-check-container">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="isMultipleWindows"
                      v-model="localStrategy.isMultipleWindows"
                    />
                    <label class="form-check-label" for="isMultipleWindows">
                      Adjust the price based on number of days-out
                    </label>
                  </div>
                </div>
                <div class="col-lg-5 col-md-5 col-sm-12">
                  <div class="form-check form-check-container">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="isAdjustByUtilization"
                      v-model="localStrategy.isAdjustByUtilization"
                    />
                    <label class="form-check-label" for="isAdjustByUtilization">
                      Adjust the price as the tee sheet fills
                    </label>
                  </div>
                </div>
              </div>
              <hr class="light-divider" />
              <div class="row strategy-action-buttons justify-content-end">
                <div class="col-lg-2 col-md-4 col-sm-6">
                  <button class="btn btn-secondary" @click.prevent="cancel">
                    Cancel
                  </button>
                </div>
                <div class="col-lg-4 col-md-5 col-sm-6">
                  <button
                    class="btn btn-primary"
                    @click.prevent="continue2"
                    :disabled="isNameNotValid"
                  >
                    Continue to Pricing Logic
                    <i class="fa fa-caret-right" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
  <StrategyCopyComponent
    :Strategy="strategy"
    :Facilities="facilities"
    :ExistingStrategies="existingStrategies"
  />
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed } from 'vue';
import { Facility, Strategy } from '../api';
import eventBus, { EventType } from '../eventBus';
import { strategyApi } from '@/apiClient';
import StrategyCopyComponent from './StrategyCopyComponent.vue';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import * as bootstrap from 'bootstrap';
import { isNameNotUnique } from '@/utils/strategyUtils';

interface DeleteStrategyResponse {
  success: boolean;
  message?: string;
}

export default defineComponent({
  name: 'StrategyComponent',
  components: {
    StrategyCopyComponent,
  },
  props: {
    strategy: {
      type: Object as PropType<Strategy>,
      required: true,
    },
    facilities: {
      type: Array as PropType<Facility[]>,
      required: true,
    },
    priceWindowId: {
      type: Number,
      required: false,
    },
    existingStrategies: {
      type: Array as PropType<Strategy[]>,
      required: false,
    },
  },
  emits: ['strategy-selected'],
  setup(props, { emit }) {
    const localStrategy = ref({ ...props.strategy });
    let localStrategyInitialName = localStrategy.value.name;
    const localFacilities = ref({ ...props.facilities });
    const toast = useToast();
    const store = useStore();
    const originalStrategyName = ref(localStrategy.value.name);

    const isAdjustByUtilization = localStrategy.value.isAdjustByUtilization;

    const isNameNotValid = computed(() => {
      if (
        localStrategy.value.name?.trim().toLowerCase() ===
        localStrategyInitialName?.trim().toLowerCase()
      ) {
        return false;
      }

      return isNameNotUnique(
        localStrategy.value.name ?? '',
        props.existingStrategies ?? []
      );
    });

    const updateIsAdjustByUtilization = (event: Event) => {
      localStrategy.value.isAdjustByUtilization = (
        event.target as HTMLInputElement
      ).checked; // Update the local strategy object
      // store.dispatch('updateIsAdjustByUtilization', target.checked);
    };

    const cancel = () => {
      // Revert localStrategy to its original prop values
      localStrategy.value = { ...props.strategy };

      const collapseElement = document.getElementById(
        `collapse-${localStrategy.value.id}`
      );
      if (collapseElement && collapseElement.classList.contains('show')) {
        const bsCollapse =
          bootstrap.Collapse.getOrCreateInstance(collapseElement);
        bsCollapse.hide();
      }
    };

    const continue2 = async () => {
      console.log('Continue clicked');
      const invalidName =
        localStrategy.value.name !== originalStrategyName.value
          ? await checkNameExists()
          : false;

      if (invalidName) {
        toast.error(
          'A strategy with this name already exists for the selected facility.'
        );
        localStrategy.value.name = originalStrategyName.value;
        return;
      }

      // Emit an event to continue to the next step
      if (localStrategy.value.id) {
        eventBus.emit(EventType.ShowPricingLogic, {
          strategyId: localStrategy.value.id,
          strategyObj: null,
        });
        emit('strategy-selected', localStrategy.value);
        saveStrategy(false);
      }
    };

    const checkNameExists = async () => {
      try {
        return (
          await strategyApi.apiStrategyExistsGet(
            localStrategy.value.facilityId,
            localStrategy.value.name || '',
            localStrategy.value.id || 0
          )
        ).data;
      } catch {
        toast.error(
          'Error checking if strategy name exists. Please try again later'
        );
        return true;
      }
    };

    const saveStrategy = async (showToast: boolean) => {
      const invalidName =
        localStrategy.value.name !== originalStrategyName.value
          ? await checkNameExists()
          : false;

      if (invalidName) {
        localStrategy.value.name = originalStrategyName.value;
        if (showToast) {
          toast.error(
            'A strategy with this name already exists for the selected facility.'
          );
        }
        return;
      }

      await strategyApi.apiStrategyPut(localStrategy.value);
      localStrategyInitialName = localStrategy.value.name;
      eventBus.emit(EventType.RefreshStrategies);
      if (showToast) {
        toast.success('Strategy Saved Successfully!');
      }
    };

    const deleteStrategy = () => {
      if (
        confirm(
          'Are you sure you want to delete this strategy? This action cannot be undone.'
        )
      ) {
        strategyApi
          .apiStrategyDelete(localStrategy.value.id)
          .then((response: any) => {
            if (
              (response.data && response.data.success === false) ||
              response.data.resultCode === 500
            ) {
              toast.error(response.data.message);
              return;
            }
            eventBus.emit(EventType.RefreshStrategies);
            toast.success('Strategy Deleted Successfully!');
          })
          .catch((error) => {
            console.error('Error deleting strategy:', error);
            const errorMessage =
              error.response?.data?.message ||
              'Failed to delete strategy. Please try again.';
            toast.error(errorMessage);
          });
      }
    };

    return {
      localStrategy,
      localFacilities,
      cancel,
      continue2,
      saveStrategy,
      deleteStrategy,
      isAdjustByUtilization,
      updateIsAdjustByUtilization,
      isNameNotValid,
    };
  },
});
</script>

<style scoped>
.accordion-button {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  min-height: 56px;
}

.accordion-button:not(.collapsed) {
  background-color: #618183;
  color: #ffffff;
}

.accordion-button:not(.collapsed) .btn-group i {
  color: #ffffff;
}

.accordion-button:focus {
  box-shadow: 0 0 0 0.25rem rgba(64, 95, 107, 0.25);
}

.accordion-body {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.accordion .accordion-item {
  margin-bottom: 0.5rem;
}

.accordion-collapse {
  transition: height 0.2s ease;
}

.strategy-options-form {
  padding: 1rem;
}

.strategy-action-buttons .btn {
  width: 100%;
  margin-top: 0.5rem;
}

.strategy-action-buttons .btn-secondary {
  background-color: transparent;
  border-color: transparent;
  color: black;
}

.strategy-action-buttons .btn-primary {
  background-color: #405f6b;
  border-color: #405f6b;
}

.form-group label {
  font-weight: 600;
  text-align: left; /* Ensures labels are left aligned */
}

.strategy-action-buttons {
  text-align: right; /* Ensures buttons are right aligned */
}

.form-check-container {
  text-align: left;
}

.form-check-input:checked {
  background-color: #405f6b;
  border-color: #405f6b;
}

.btn-group i {
  cursor: pointer;
  color: #405f6b;
  transition: color 0.3s ease;
  padding: 8px;
  font-size: 1.5em;
}

.strategy-action-icon--disabled {
  color: #ccc !important;
  cursor: not-allowed !important;
}

.strategy-action-icon:hover:not(.action-icon--disabled) {
  color: #00bfff !important;
}
</style>
